import { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Play, Image as ImageIcon, Download, ZoomIn, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Helper per determinare il tipo di file
const isHeicFile = (media) => {
    if (!media) return false;
    
    // Prima controlla il content_type (priorità - indica il formato effettivo salvato)
    const contentType = (media.content_type || '').toLowerCase();
    if (contentType === 'image/jpeg' || contentType === 'image/jpg' || contentType === 'image/png') {
        return false; // È stato convertito, non è più HEIC
    }
    
    // Se il content_type indica HEIC
    if (contentType.includes('heic') || contentType.includes('heif')) return true;
    
    // Controlla anche il filename salvato (non l'originale)
    const filename = (media.filename || '').toLowerCase();
    if (filename.endsWith('.heic') || filename.endsWith('.heif')) return true;
    
    return false;
};

const isBrowserSupportedImage = (media) => {
    if (!media) return false;
    const contentType = (media.content_type || '').toLowerCase();
    const filename = (media.filename || '').toLowerCase(); // Usa filename, non original_filename
    
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
    if (supportedTypes.some(type => contentType.includes(type))) return true;
    
    const supportedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    if (supportedExtensions.some(ext => filename.endsWith(ext))) return true;
    
    return false;
};

const isVideoFile = (media) => {
    if (!media) return false;
    const contentType = (media.content_type || '').toLowerCase();
    const filename = (media.original_filename || media.filename || '').toLowerCase();
    
    if (contentType.startsWith('video/')) return true;
    
    const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];
    return videoExtensions.some(ext => filename.endsWith(ext));
};

// Componente Lightbox per visualizzare immagini ingrandite
function ImageLightbox({ imageUrl, filename, onClose }) {
    // Chiudi con ESC
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    return (
        <div 
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
                <X className="w-6 h-6 text-white" />
            </button>
            <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <img
                    src={imageUrl}
                    alt={filename}
                    className="max-w-full max-h-[85vh] object-contain"
                />
                <p className="text-white/60 text-center mt-2 text-sm">{filename}</p>
            </div>
        </div>
    );
}

function VideoPlayerModal({ streamUrl, filename, onClose, onDownload }) {
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    return (
        <div data-testid="video-player-modal" className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
            <button type="button" data-testid="video-player-close-btn" onClick={onClose}
                className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
                <X className="w-6 h-6 text-white" />
            </button>
            <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
                {!streamUrl ? (
                    <div className="flex justify-center py-20">
                        <div className="w-8 h-8 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
                    </div>
                ) : failed ? (
                    <div data-testid="video-player-error" className="text-center text-white/80 py-16">
                        <p className="mb-4">Il browser non riesce a riprodurre questo formato video.</p>
                        <button type="button" data-testid="video-player-fallback-download-btn" onClick={onDownload}
                            className="px-4 py-2 bg-[#00FF41] text-black font-medium inline-flex items-center gap-2">
                            <Download className="w-4 h-4" /> Scarica il video
                        </button>
                    </div>
                ) : (
                    <video data-testid="video-player" src={streamUrl} controls autoPlay playsInline preload="metadata"
                        onError={() => setFailed(true)} className="w-full max-h-[80vh] bg-black" />
                )}
                <div className="flex items-center justify-between mt-2 gap-4">
                    <p className="text-white/60 text-sm truncate">{filename}</p>
                    <button type="button" data-testid="video-player-download-btn" onClick={onDownload}
                        className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1 shrink-0">
                        <Download className="w-4 h-4" /> Scarica
                    </button>
                </div>
            </div>
        </div>
    );
}

function VideoThumbnail({ src }) {
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    if (!src || failed) return null;
    return (
        <video
            src={`${src}#t=0.5`}
            muted
            playsInline
            preload="metadata"
            onLoadedData={() => setReady(true)}
            onError={() => setFailed(true)}
            className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-300 ${ready ? 'opacity-100' : 'opacity-0'}`}
        />
    );
}

export default function EventMediaItem({ caseId, dayId, eventId, media, onDelete }) {
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [showLightbox, setShowLightbox] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [streamUrl, setStreamUrl] = useState(null);
    const [thumbUrl, setThumbUrl] = useState(null);

    const fetchStreamUrl = async () => {
        const token = localStorage.getItem('token');
        const res = await axios.get(
            `${API_URL}/cases/${caseId}/activity-days/${dayId}/events/${eventId}/media/${media.id}/stream-url`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return `${BACKEND_URL}${res.data.url}`;
    };

    const openVideo = async (e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        setStreamUrl(null);
        setShowVideo(true);
        try {
            setStreamUrl(await fetchStreamUrl());
        } catch (err) {
            setShowVideo(false);
            alert('Impossibile aprire il video');
        }
    };

    const isHeic = isHeicFile(media);
    const isSupportedImage = isBrowserSupportedImage(media) && !isHeic;
    const isVideo = isVideoFile(media);

    useEffect(() => {
        if (!isVideo) return;
        let mounted = true;
        fetchStreamUrl().then((url) => { if (mounted) setThumbUrl(url); }).catch(() => {});
        return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVideo, media.id]);

    useEffect(() => {
        let mounted = true;
        let objectUrl = null;
        const token = localStorage.getItem('token');
        
        if (isSupportedImage) {
            axios.get(
                `${API_URL}/cases/${caseId}/activity-days/${dayId}/events/${eventId}/media/${media.id}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            )
            .then(response => {
                if (mounted) {
                    objectUrl = URL.createObjectURL(response.data);
                    setImageUrl(objectUrl);
                    setLoading(false);
                }
            })
            .catch(err => {
                if (mounted) {
                    setError(true);
                    setLoading(false);
                }
            });
        } else {
            setLoading(false);
        }

        return () => {
            mounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId, dayId, eventId, media.id, isSupportedImage]);

    const handleDownload = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        if (downloading) return;
        
        const token = localStorage.getItem('token');
        try {
            setDownloading(true);
            const response = await axios.get(
                `${API_URL}/cases/${caseId}/activity-days/${dayId}/events/${eventId}/media/${media.id}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = media.original_filename || `file_${media.id}`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (err) {
            const errorMsg = err.response?.status 
                ? `Errore ${err.response.status}: ${err.response.statusText || 'File non trovato'}`
                : 'Errore di connessione al server';
            alert(errorMsg);
        } finally {
            setDownloading(false);
        }
    };

    // Render per file HEIC (placeholder con download)
    if (isHeic) {
        return (
            <div 
                data-testid={`media-heic-${media.id}`}
                className={`relative group w-16 h-16 bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-500/30 overflow-hidden cursor-pointer ${downloading ? 'opacity-50' : ''}`}
                onClick={handleDownload}
                title={`Clicca per scaricare ${media.original_filename || 'HEIC'}`}>
                <div className="w-full h-full flex flex-col items-center justify-center">
                    {downloading ? (
                        <div className="w-4 h-4 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                    ) : (
                        <>
                            <ImageIcon className="w-5 h-5 text-purple-400 mb-1" />
                            <span className="text-[8px] text-purple-300 font-bold">HEIC</span>
                        </>
                    )}
                </div>
                <div className="absolute inset-0 bg-purple-500/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Download className="w-5 h-5 text-white" />
                </div>
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete(media.id); }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-10"
                >
                    <Trash2 className="w-3 h-3" />
                </button>
            </div>
        );
    }

    return (
        <>
            {/* Lightbox */}
            {showLightbox && imageUrl && (
                <ImageLightbox 
                    imageUrl={imageUrl} 
                    filename={media.original_filename} 
                    onClose={() => setShowLightbox(false)} 
                />
            )}
            {showVideo && (
                <VideoPlayerModal
                    streamUrl={streamUrl}
                    filename={media.original_filename}
                    onClose={() => setShowVideo(false)}
                    onDownload={handleDownload}
                />
            )}
            
            <div 
                data-testid={`media-item-${media.id}`}
                className="relative group w-16 h-16 bg-black border border-white/10 overflow-hidden">
                {loading ? (
                    <div className="w-full h-full flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="w-full h-full flex items-center justify-center cursor-pointer" onClick={handleDownload} title="Clicca per scaricare">
                        <ImageIcon className="w-4 h-4 text-red-500" />
                    </div>
                ) : isSupportedImage && imageUrl ? (
                    <>
                        <img
                            src={imageUrl}
                            alt={media.original_filename}
                            className="w-full h-full object-cover"
                        />
                        {/* Pulsante zoom al centro su hover */}
                        <div 
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer"
                            onClick={() => setShowLightbox(true)}
                        >
                            <ZoomIn className="w-6 h-6 text-white" />
                        </div>
                    </>
                ) : isVideo ? (
                    <div data-testid={`media-video-play-${media.id}`} className="relative w-full h-full flex flex-col items-center justify-center bg-black/50 cursor-pointer group/video" onClick={openVideo} title="Clicca per riprodurre">
                        <VideoThumbnail src={thumbUrl} />
                        <div className="relative z-[1] w-7 h-7 rounded-full bg-black/60 border border-[#00FF41]/60 flex items-center justify-center group-hover/video:scale-110 transition-transform">
                            <Play className="w-3.5 h-3.5 text-[#00FF41] ml-0.5" />
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full flex items-center justify-center cursor-pointer" onClick={handleDownload} title="Clicca per scaricare">
                        <ImageIcon className="w-4 h-4 text-white/40" />
                    </div>
                )}
                
                {/* Pulsante elimina */}
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete(media.id); }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-10"
                >
                    <Trash2 className="w-3 h-3" />
                </button>
            </div>
        </>
    );
}
